/**
 * Telegram Auth Logger
 *
 * Dedicated logging service for Telegram authentication flows.
 * Logs initData, API calls to /telegram/initiate and /telegram/link endpoints.
 *
 * Security Note: initData is logged for debugging but sensitive parts
 * (like hash) should be partially masked in production.
 */

const IS_DEVELOPMENT = 'development'

interface TelegramInitiateLogData {
  initData: string
  deviceToken: string
  platform: string
  language: string
  appVersion: string
}

interface TelegramInitiateResponseLogData {
  telegramId: string
  hasAccessToken: boolean
  hasRefreshToken: boolean
}

interface TelegramLinkLogData {
  telegramId: string
  hasEmail: boolean
  hasPhoneNumber: boolean
  platform: string
  language: string
}

interface TelegramLinkResponseLogData {
  success: boolean
  hasAccessToken: boolean
  hasRefreshToken: boolean
}

/**
 * Masks sensitive data in initData for logging
 * Keeps first 50 chars and last 20 chars visible, masks the rest
 */
function maskInitData(initData: string): string {
  if (initData.length <= 100) {
    return initData
  }
  const start = initData.substring(0, 50)
  const end = initData.substring(initData.length - 20)
  return `${start}...MASKED...${end}`
}

/**
 * Gets current timestamp in ISO format
 */
function getTimestamp(): string {
  return new Date().toISOString()
}

/**
 * Logs telegram/initiate API call
 */
export function logTelegramInitiate(data: TelegramInitiateLogData): void {
  const logEntry = {
    timestamp: getTimestamp(),
    event: 'TELEGRAM_INITIATE_REQUEST',
    initData: IS_DEVELOPMENT ? data.initData : maskInitData(data.initData),
    initDataLength: data.initData.length,
    deviceToken: data.deviceToken ? `${data.deviceToken.substring(0, 8)}...` : 'none',
    platform: data.platform,
    language: data.language,
    appVersion: data.appVersion,
  }

  console.log('[TelegramAuth] Initiate Request:', logEntry)
}

/**
 * Logs telegram/initiate API response
 */
export function logTelegramInitiateResponse(data: TelegramInitiateResponseLogData): void {
  const logEntry = {
    timestamp: getTimestamp(),
    event: 'TELEGRAM_INITIATE_RESPONSE',
    telegramId: data.telegramId,
    isLinked: data.hasAccessToken && data.hasRefreshToken,
    hasAccessToken: data.hasAccessToken,
    hasRefreshToken: data.hasRefreshToken,
  }

  console.log('[TelegramAuth] Initiate Response:', logEntry)
}

/**
 * Logs telegram/initiate API error
 */
export function logTelegramInitiateError(error: unknown): void {
  const logEntry = {
    timestamp: getTimestamp(),
    event: 'TELEGRAM_INITIATE_ERROR',
    error: error instanceof Error ? error.message : String(error),
  }

  console.error('[TelegramAuth] Initiate Error:', logEntry)
}

/**
 * Logs telegram/link API call
 */
export function logTelegramLink(data: TelegramLinkLogData): void {
  const logEntry = {
    timestamp: getTimestamp(),
    event: 'TELEGRAM_LINK_REQUEST',
    telegramId: data.telegramId,
    hasEmail: data.hasEmail,
    hasPhoneNumber: data.hasPhoneNumber,
    platform: data.platform,
    language: data.language,
  }

  console.log('[TelegramAuth] Link Request:', logEntry)
}

/**
 * Logs telegram/link API response
 */
export function logTelegramLinkResponse(data: TelegramLinkResponseLogData): void {
  const logEntry = {
    timestamp: getTimestamp(),
    event: 'TELEGRAM_LINK_RESPONSE',
    success: data.success,
    hasAccessToken: data.hasAccessToken,
    hasRefreshToken: data.hasRefreshToken,
  }

  console.log('[TelegramAuth] Link Response:', logEntry)
}

/**
 * Logs telegram/link API error
 */
export function logTelegramLinkError(error: unknown): void {
  const logEntry = {
    timestamp: getTimestamp(),
    event: 'TELEGRAM_LINK_ERROR',
    error: error instanceof Error ? error.message : String(error),
  }

  console.error('[TelegramAuth] Link Error:', logEntry)
}

/**
 * Logs raw initData for debugging purposes
 * Only logs in development environment
 */
export function logInitDataRaw(initDataRaw: string): void {
  if (!IS_DEVELOPMENT) {
    console.log('[TelegramAuth] initData received (length:', initDataRaw.length, ')')
    return
  }

  console.log('[TelegramAuth] initData raw:', initDataRaw)

  // Parse and log initData components for debugging
  try {
    const params = new URLSearchParams(initDataRaw)
    const parsed: Record<string, string> = {}
    params.forEach((value, key) => {
      // Mask the hash for security
      if (key === 'hash') {
        parsed[key] = `${value.substring(0, 8)}...`
      } else if (key === 'user') {
        // Parse and log user object
        try {
          const user = JSON.parse(value)
          parsed[key] = JSON.stringify({
            id: user.id,
            first_name: user.first_name,
            username: user.username,
          })
        } catch {
          parsed[key] = value.substring(0, 50) + '...'
        }
      } else {
        parsed[key] = value
      }
    })
    console.log('[TelegramAuth] initData parsed:', parsed)
  } catch (e) {
    console.log('[TelegramAuth] Could not parse initData')
  }
}

export const telegramAuthLogger = {
  logTelegramInitiate,
  logTelegramInitiateResponse,
  logTelegramInitiateError,
  logTelegramLink,
  logTelegramLinkResponse,
  logTelegramLinkError,
  logInitDataRaw,
}
